import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { 
  Wallet, 
  Loader2,
  ExternalLink,
  Clock,
  CheckCircle,
  ArrowRight,
  RefreshCw,
  Copy,
  CreditCard,
  Bitcoin,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Simple QR Code component using an API
const QRCode = ({ value, size = 150 }) => {
  if (!value) return null;
  return (
    <img 
      src={`https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&bgcolor=1e293b&color=22c55e`}
      alt="QR Code"
      width={size}
      height={size}
      className="rounded"
    />
  );
};

// Crypto icons
const CryptoIcon = ({ type }) => {
  const icons = { btc: '₿', ltc: 'Ł', usdt: '₮' };
  const colors = {
    btc: 'bg-orange-500/20 text-orange-400',
    ltc: 'bg-slate-500/20 text-slate-300',
    usdt: 'bg-green-500/20 text-green-400'
  };
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-lg font-bold ${colors[type] || 'bg-slate-500/20 text-slate-300'}`}>
      {icons[type] || '?'}
    </span>
  );
};

// Conversion rate: $3 = 1000 Robux
const ROBUX_TO_USD_RATE = 0.003; // $0.003 per Robux (or $3 per 1000)

export default function Deposit() {
  const { user, token, api, refreshUser } = useAuth();
  const [activeMethod, setActiveMethod] = useState('stripe'); // 'stripe', 'crypto', 'robux'
  const [loading, setLoading] = useState(false);
  
  // Stripe state - Robux amount
  const [stripeRobuxAmount, setStripeRobuxAmount] = useState('');
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Crypto state
  const [cryptoAddresses, setCryptoAddresses] = useState(null);
  const [cryptoRates, setCryptoRates] = useState(null);
  const [selectedCrypto, setSelectedCrypto] = useState('btc');
  const [copied, setCopied] = useState('');
  const [loadingCrypto, setLoadingCrypto] = useState(false);
  
  // Robux P2P state
  const [amount, setAmount] = useState('');
  const [myOrders, setMyOrders] = useState([]);
  const [pendingOrders, setPendingOrders] = useState({ pending_deposits: [], pending_withdraws: [] });
  const [currentOrder, setCurrentOrder] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    fetchData();
    checkUrlParams();
  }, [token]);

  // Check URL for Stripe return
  const checkUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success');
    
    if (sessionId && success) {
      setCheckingPayment(true);
      pollStripeStatus(sessionId);
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const pollStripeStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      toast.error('Payment status check timed out. Please contact support.');
      return;
    }

    try {
      const res = await api.get(`/stripe/status/${sessionId}`);
      
      if (res.data.payment_status === 'paid') {
        toast.success(res.data.message);
        setCheckingPayment(false);
        refreshUser();
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired');
        setCheckingPayment(false);
        return;
      }
      
      setTimeout(() => pollStripeStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setCheckingPayment(false);
      toast.error('Error checking payment status');
    }
  };

  const fetchData = async () => {
    if (!token) return;
    
    try {
      const [ordersRes, pendingRes] = await Promise.all([
        api.get('/robux/my-orders'),
        api.get('/robux/pending-orders')
      ]);
      
      setMyOrders(ordersRes.data.orders || []);
      setPendingOrders(pendingRes.data);
      
      const activeDeposit = ordersRes.data.orders?.find(o => 
        o.type === 'deposit' && ['pending', 'processing'].includes(o.status)
      );
      if (activeDeposit) setCurrentOrder(activeDeposit);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  // Fetch crypto data when crypto tab is selected
  useEffect(() => {
    if (activeMethod === 'crypto' && token && !cryptoAddresses) {
      fetchCryptoData();
    }
  }, [activeMethod, token]);

  const fetchCryptoData = async () => {
    setLoadingCrypto(true);
    try {
      const [addressRes, ratesRes] = await Promise.all([
        api.get('/deposit/crypto/addresses'),
        api.get('/deposit/crypto/rates')
      ]);
      setCryptoAddresses(addressRes.data.addresses || {});
      setCryptoRates(ratesRes.data);
    } catch (error) {
      console.error('Failed to fetch crypto data');
      toast.error('Failed to load crypto deposit info');
    } finally {
      setLoadingCrypto(false);
    }
  };

  // Poll for Robux order updates
  useEffect(() => {
    if (currentOrder && currentOrder.status === 'pending') {
      const interval = setInterval(async () => {
        try {
          const res = await api.get(`/robux/order/${currentOrder.order_id}`);
          if (res.data.status !== currentOrder.status) {
            setCurrentOrder(res.data);
            fetchData();
            refreshUser();
            if (res.data.status === 'completed') {
              toast.success(`Deposit complete! R$${res.data.amount?.toLocaleString()} added to your balance!`);
            }
          }
        } catch (e) {}
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [currentOrder]);

  const handleStripeCheckout = async () => {
    const robuxAmount = parseInt(stripeRobuxAmount);
    if (!robuxAmount || robuxAmount < 100) {
      toast.error('Minimum purchase is R$100');
      return;
    }
    if (robuxAmount > 333333) {
      toast.error('Maximum purchase is R$333,333');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/stripe/checkout', {
        robux_amount: robuxAmount,
        origin_url: window.location.origin
      });
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
    } finally {
      setLoading(false);
    }
  };

  const copyAddress = (address) => {
    if (!address) return;
    navigator.clipboard.writeText(address);
    setCopied(address);
    toast.success('Address copied!');
    setTimeout(() => setCopied(''), 2000);
  };

  const handleRobuxDeposit = async () => {
    const depositAmount = parseFloat(amount);
    if (!depositAmount || depositAmount < 10) {
      toast.error('Minimum deposit is R$10');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/robux/deposit', { amount: depositAmount });
      toast.success(res.data.message);
      setCurrentOrder(res.data);
      setAmount('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create deposit order');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPurchase = async () => {
    if (!currentOrder) return;
    
    setConfirming(true);
    try {
      const res = await api.post(`/robux/confirm-purchase/${currentOrder.order_id}`);
      toast.success(res.data.message);
      setCurrentOrder(null);
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm purchase');
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!currentOrder) return;
    try {
      await api.post(`/robux/cancel/${currentOrder.order_id}`);
      toast.success('Order cancelled');
      setCurrentOrder(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const quickUsdAmounts = [1000, 5000, 10000, 50000, 100000];
  const quickRobuxAmounts = [100, 500, 1000, 5000, 10000];

  // Get current crypto address
  const getCurrentCryptoAddress = () => {
    if (!cryptoAddresses || !selectedCrypto) return null;
    const addrData = cryptoAddresses[selectedCrypto];
    if (!addrData) return null;
    return typeof addrData === 'string' ? addrData : addrData.address;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 p-4 md:p-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <Wallet className="text-green-400" />
          Deposit
        </h1>
        <p className="text-slate-400 mb-6">Add funds to your account</p>

        {/* Payment checking overlay */}
        {checkingPayment && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl p-8 text-center">
              <Loader2 className="animate-spin text-green-400 mx-auto mb-4" size={48} />
              <p className="text-white text-lg">Verifying your payment...</p>
              <p className="text-slate-400 text-sm mt-2">Please wait, do not close this page.</p>
            </div>
          </div>
        )}

        {/* Method Tabs */}
        <div className="flex gap-2 mb-6 bg-slate-800/50 p-1 rounded-lg">
          <button
            onClick={() => setActiveMethod('stripe')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
              activeMethod === 'stripe' ? 'bg-green-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <CreditCard size={18} /> Card / USD
          </button>
          <button
            onClick={() => setActiveMethod('crypto')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
              activeMethod === 'crypto' ? 'bg-orange-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Bitcoin size={18} /> Crypto
          </button>
          <button
            onClick={() => setActiveMethod('robux')}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
              activeMethod === 'robux' ? 'bg-blue-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            <Wallet size={18} /> Robux (P2P)
          </button>
        </div>

        {/* STRIPE DEPOSITS - Custom Amount */}
        {activeMethod === 'stripe' && (
          <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-2">Buy Robux with Card</h2>
            <p className="text-slate-400 text-sm mb-4">R$1,000 = $3 USD</p>
            
            {/* 10x Wager Warning */}
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
              <p className="text-orange-400 font-medium mb-1">10x Wager Requirement</p>
              <p className="text-slate-300 text-sm">
                Card deposits require 10x wagering before withdrawal.
              </p>
            </div>
            
            {/* Robux Amount Input */}
            <div className="mb-4">
              <label className="text-slate-300 text-sm mb-2 block">Enter Robux Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-green-400 font-bold text-xl">R$</span>
                <input
                  type="number"
                  value={stripeRobuxAmount}
                  onChange={(e) => setStripeRobuxAmount(e.target.value)}
                  placeholder="0"
                  min="100"
                  max="333333"
                  className="w-full bg-slate-900/50 border border-slate-600 rounded-lg pl-12 pr-4 py-4 text-2xl text-white focus:outline-none focus:border-green-500"
                />
              </div>
            </div>

            {/* Quick Amounts */}
            <div className="flex flex-wrap gap-2 mb-6">
              {quickUsdAmounts.map(amt => (
                <button
                  key={amt}
                  onClick={() => setStripeRobuxAmount(amt.toString())}
                  className={`px-4 py-2 rounded-lg transition ${
                    stripeRobuxAmount === amt.toString() 
                      ? 'bg-green-600 text-white' 
                      : 'bg-slate-700/50 hover:bg-slate-600/50 text-slate-300'
                  }`}
                >
                  R${amt.toLocaleString()}
                </button>
              ))}
            </div>

            {/* You'll Pay */}
            {stripeRobuxAmount && parseInt(stripeRobuxAmount) >= 100 && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-slate-400 text-sm">You'll receive:</p>
                    <p className="text-2xl font-bold text-green-400">
                      R${parseInt(stripeRobuxAmount).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-400 text-sm">You'll pay:</p>
                    <p className="text-2xl font-bold text-white">
                      ${((parseInt(stripeRobuxAmount) / 1000) * 3).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button 
              onClick={handleStripeCheckout}
              disabled={loading || !stripeRobuxAmount || parseInt(stripeRobuxAmount) < 100}
              className="w-full bg-green-600 hover:bg-green-500 py-6 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2" />
                  Redirecting...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2" />
                  Buy R${stripeRobuxAmount ? parseInt(stripeRobuxAmount).toLocaleString() : '0'} for ${stripeRobuxAmount ? ((parseInt(stripeRobuxAmount) / 1000) * 3).toFixed(2) : '0.00'}
                </>
              )}
            </Button>
            
            <p className="text-slate-500 text-xs mt-4 text-center">
              Secure payment powered by Stripe. Min R$100, Max R$333,333.
            </p>
          </div>
        )}

        {/* CRYPTO DEPOSITS */}
        {activeMethod === 'crypto' && (
          <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-4">Crypto Deposit</h2>
            
            {/* 10x Wager Warning */}
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
              <p className="text-orange-400 font-medium mb-1">10x Wager Requirement</p>
              <p className="text-slate-300 text-sm">
                Crypto deposits require 10x wagering before withdrawal.
              </p>
            </div>
            
            {/* Crypto Selector */}
            <div className="flex gap-2 mb-6">
              {['btc', 'ltc', 'usdt'].map(crypto => (
                <button
                  key={crypto}
                  onClick={() => setSelectedCrypto(crypto)}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                    selectedCrypto === crypto ? 'bg-slate-700 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700/50'
                  }`}
                >
                  <CryptoIcon type={crypto} />
                  {crypto.toUpperCase()}
                </button>
              ))}
            </div>

            {loadingCrypto ? (
              <div className="text-center py-8">
                <Loader2 className="animate-spin text-slate-400 mx-auto" size={32} />
                <p className="text-slate-400 mt-2">Loading crypto info...</p>
              </div>
            ) : (
              <>
                {/* Rates */}
                {cryptoRates?.rates?.[selectedCrypto] && (
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <p className="text-slate-400 text-sm mb-1">Current Rate:</p>
                    <p className="text-white font-bold text-lg">
                      1 {selectedCrypto.toUpperCase()} = R${cryptoRates.rates[selectedCrypto]?.robux_per_coin?.toLocaleString() || 'N/A'}
                    </p>
                  </div>
                )}

                {/* Deposit Address */}
                {getCurrentCryptoAddress() ? (
                  <div className="bg-slate-900/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm mb-2">Send {selectedCrypto.toUpperCase()} to:</p>
                    <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-3 mb-4">
                      <code className="text-green-400 text-sm flex-1 break-all">
                        {getCurrentCryptoAddress()}
                      </code>
                      <button
                        onClick={() => copyAddress(getCurrentCryptoAddress())}
                        className="p-2 hover:bg-slate-700 rounded transition flex-shrink-0"
                      >
                        {copied === getCurrentCryptoAddress() ? (
                          <CheckCircle className="text-green-400" size={18} />
                        ) : (
                          <Copy className="text-slate-400" size={18} />
                        )}
                      </button>
                    </div>
                    
                    <div className="flex justify-center">
                      <QRCode value={getCurrentCryptoAddress()} size={150} />
                    </div>
                    
                    <p className="text-slate-500 text-xs mt-4 text-center">
                      Send only {selectedCrypto.toUpperCase()} to this address. 
                      Deposits credited after network confirmation.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-slate-400">No address available for {selectedCrypto.toUpperCase()}</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ROBUX P2P DEPOSITS */}
        {activeMethod === 'robux' && (
          <div className="space-y-6">
            {/* Cookie Warning */}
            {!user?.roblox_cookie && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="text-red-400 mt-0.5" size={18} />
                  <div>
                    <p className="text-red-400 font-medium">Roblox Cookie Required</p>
                    <p className="text-slate-300 text-sm">
                      To enable auto-purchase, you need to link your Roblox cookie in Settings.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Current Active Order */}
            {currentOrder && (
              <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
                {currentOrder.status === 'pending' ? (
                  <>
                    <div className="flex items-center gap-2 text-yellow-400 mb-4">
                      <Clock className="animate-pulse" />
                      <span className="font-semibold">Waiting for Match...</span>
                    </div>
                    <p className="text-slate-300 mb-4">
                      Your deposit order for <span className="text-green-400 font-bold">R${currentOrder.amount?.toLocaleString()}</span> is waiting to be matched with a withdrawer.
                    </p>
                    <p className="text-slate-400 text-sm mb-4">
                      When a withdrawer creates a gamepass at this price, it will be auto-purchased using your linked Roblox account.
                    </p>
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
                      <RefreshCw size={14} className="animate-spin" />
                      Auto-checking for matching withdrawals...
                    </div>
                    <Button variant="outline" onClick={handleCancelOrder} className="text-red-400 border-red-400/50">
                      Cancel Order
                    </Button>
                  </>
                ) : currentOrder.status === 'completed' ? (
                  <>
                    <div className="flex items-center gap-2 text-green-400 mb-4">
                      <CheckCircle />
                      <span className="font-semibold">Auto-Completed!</span>
                    </div>
                    <p className="text-slate-300 mb-4">
                      Gamepass purchased automatically! <span className="text-green-400 font-bold">R${currentOrder.amount?.toLocaleString()}</span> has been added to your balance.
                    </p>
                    <Button 
                      onClick={() => {setCurrentOrder(null); fetchData();}}
                      className="bg-slate-700"
                    >
                      Start New Deposit
                    </Button>
                  </>
                ) : null}
              </div>
            )}

            {/* Deposit Form */}
            {!currentOrder && (
              <div className="bg-slate-800/80 rounded-xl p-6 border border-slate-700">
                <h2 className="text-xl font-semibold text-white mb-2">Robux P2P Deposit</h2>
                <p className="text-slate-400 text-sm mb-4">Automatic purchase. No manual steps required.</p>
                
                <div className="mb-4">
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-green-400 font-bold">R$</span>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0"
                      className="w-full bg-slate-900/50 border border-slate-600 rounded-lg pl-12 pr-4 py-4 text-2xl text-white focus:outline-none focus:border-green-500"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {quickRobuxAmounts.map(amt => (
                    <button
                      key={amt}
                      onClick={() => setAmount(amt.toString())}
                      className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 rounded-lg transition"
                    >
                      R${amt.toLocaleString()}
                    </button>
                  ))}
                </div>

                {pendingOrders.withdraw_count > 0 && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 mb-4">
                    <p className="text-green-400 text-sm">
                      {pendingOrders.withdraw_count} withdraw order(s) with gamepasses ready - instant completion possible!
                    </p>
                  </div>
                )}

                <Button 
                  onClick={handleRobuxDeposit} 
                  disabled={loading || !amount || !user?.roblox_cookie}
                  className="w-full bg-blue-600 hover:bg-blue-500 py-6 text-lg"
                >
                  {loading ? <Loader2 className="animate-spin mr-2" /> : <ArrowRight className="mr-2" />}
                  Deposit R${amount ? parseFloat(amount).toLocaleString() : '0'}
                </Button>
                
                {!user?.roblox_cookie && (
                  <p className="text-red-400 text-sm mt-4 text-center">
                    Link your Roblox cookie in Settings to enable deposits
                  </p>
                )}
              </div>
            )}

            {/* How it works */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-3">How Auto-Purchase Works</h3>
              <ol className="space-y-2 text-slate-400 text-sm">
                <li className="flex gap-2">
                  <span className="text-green-400 font-bold">1.</span>
                  Enter the amount of Robux you want to deposit
                </li>
                <li className="flex gap-2">
                  <span className="text-green-400 font-bold">2.</span>
                  Your order waits for a matching withdrawal with a gamepass
                </li>
                <li className="flex gap-2">
                  <span className="text-green-400 font-bold">3.</span>
                  When matched, the gamepass is auto-purchased using your Roblox account
                </li>
                <li className="flex gap-2">
                  <span className="text-green-400 font-bold">4.</span>
                  Balance is credited instantly after purchase
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
